import { useMemo, useState } from "react";
import { Pencil, Trash2, Plus, ChevronDown, ChevronRight, CornerDownRight, Lock } from "lucide-react";
import { fmtTL } from "../../lib/format";
import { FilterTh } from "../ColFilters";
import { SVC_COLS, svcTree, BasisBadge, isProtectedSvc } from "./servicesShared";

const TH = "px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold";
const sumFin = (a, b) => ({ revenue: a.revenue + b.revenue, expense: a.expense + b.expense, diff: a.diff + b.diff });

// hizmet satırı; depth 1 = kalem (basamaklı), üst hizmette kalemler toplamı gösterilir
function ServiceRow({ s, depth = 0, fin, rolled, parentBasis, open, onToggle, onAddKalem, onEdit, onRemove }) {
  const pcount = (s.project_ids || []).length;
  const kalems = s.kalems || [];
  const locked = isProtectedSvc(s);
  return (
    <tr className={`border-t border-slate-100 ${depth ? "bg-slate-50/60" : ""}`} data-testid={`svc-row-${s.id}`} data-depth={depth} data-locked={locked ? "1" : undefined}>
      <td className="px-3 py-2 text-xs font-medium">
        <span className={`inline-flex items-center gap-1 ${depth ? "pl-6 text-slate-700" : ""}`}>
          {depth > 0 && <CornerDownRight className="w-3.5 h-3.5 text-slate-400" />}
          {kalems.length > 0 && (
            <button type="button" onClick={onToggle} data-testid={`svc-kalem-toggle-${s.id}`} className="text-slate-500 hover:text-slate-900" title={open ? "Kalemleri gizle" : "Kalemleri göster"}>
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          )}
          {locked && <Lock className="w-3 h-3 text-slate-500" data-testid={`svc-locked-${s.id}`} title="Sabit hizmet — silinemez, adı değişmez; hakediş hesabı kalemlerine bağlıdır" />}
          {s.name}
          <BasisBadge basis={s.billing_basis || parentBasis} inherited={!s.billing_basis && !!parentBasis} testid={`svc-basis-${s.id}`} />
          {kalems.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-bold" data-testid={`svc-kalem-count-${s.id}`}>{kalems.length} KALEM</span>}
          {s.scope === "projects" && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold" data-testid={`svc-scope-${s.id}`}>{pcount > 0 ? `${pcount} PROJE` : "PROJE YOK"}</span>}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">{s.unit}</td>
      <td className="numeric-cell px-3 py-2 text-xs text-emerald-700 font-semibold" data-testid={`svc-revenue-${s.id}`} title={rolled ? "Kalemler dahil toplam" : undefined}>{fmtTL(fin.revenue)}</td>
      <td className="numeric-cell px-3 py-2 text-xs text-red-700 font-semibold" data-testid={`svc-expense-${s.id}`}>{fmtTL(fin.expense)}</td>
      <td className={`numeric-cell px-3 py-2 text-xs font-bold ${fin.diff >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={`svc-diff-${s.id}`}>{fmtTL(fin.diff)}</td>
      <td className="px-3 py-2 text-xs" data-testid={`svc-locs-${s.id}`}>{(s.location_ids || []).length > 0 ? `${s.location_ids.length} lokasyon` : "Tümü"}</td>
      <td className="px-3 py-2 text-xs">{s.status}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {depth === 0 && onAddKalem && (
          <button onClick={() => onAddKalem(s)} data-testid={`svc-add-kalem-${s.id}`} title="Bu hizmetin altına kalem aç (örn. Temizlik Görevlisi, Şef)"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-slate-300 bg-white text-[10px] font-semibold text-slate-700 hover:bg-slate-50 mr-2">
            <Plus className="w-3 h-3" /> Kalem Ekle
          </button>
        )}
        <button onClick={() => onEdit(s)} className="text-slate-600 hover:text-slate-900 mr-2" data-testid={`svc-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        {locked
          ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 cursor-not-allowed" data-testid={`svc-delete-locked-${s.id}`} title="Sabit hizmet — silinemez"><Lock className="w-3.5 h-3.5" /> Sabit</span>
          : <button onClick={() => onRemove(s.id)} className="text-red-700 hover:text-red-900" data-testid={`svc-delete-${s.id}`} title={kalems.length ? "Hizmeti ve altındaki kalemleri sil" : "Hizmeti sil"}><Trash2 className="w-3.5 h-3.5" /></button>}
      </td>
    </tr>
  );
}

// başlık altındaki hizmetler: kolon filtreleri + Gelir / Gider / Fark; kalemler üst hizmetin altında basamaklı, üstte toplam
export default function ServiceTable({ rows, visibleIds, hf, setHf, autoOpts, svcFin, onEdit, onRemove, onAddKalem }) {
  const [closed, setClosed] = useState({});
  const tree = useMemo(() => svcTree(rows).filter((p) => visibleIds.has(p.id) || p.kalems.some((k) => visibleIds.has(k.id))), [rows, visibleIds]);
  const th = (c) => (
    <th key={c.key} className={`${TH} text-left`}>
      <FilterTh col={c} hf={hf} setHf={setHf} testid={`svc-hf-${c.key}`} autoOptions={autoOpts[c.key]} />
    </th>
  );
  return (
    <table className="w-full tbl">
      <thead className="bg-slate-50">
        <tr>
          {SVC_COLS.slice(0, 2).map(th)}
          {["Gelir", "Gider", "Fark"].map((h) => <th key={h} className={`${TH} text-right`}>{h}</th>)}
          {SVC_COLS.slice(2).map(th)}
          <th className="px-3 py-2 text-[10px] text-right"></th>
        </tr>
      </thead>
      <tbody>
        {tree.map((p) => {
          const open = !closed[p.id];
          const fin = p.kalems.reduce((a, k) => sumFin(a, svcFin(k.id)), svcFin(p.id));
          return [
            <ServiceRow key={p.id} s={p} fin={fin} rolled={p.kalems.length > 0} open={open} onToggle={() => setClosed((c) => ({ ...c, [p.id]: !c[p.id] }))} onAddKalem={onAddKalem} onEdit={onEdit} onRemove={onRemove} />,
            ...(open ? p.kalems.map((k) => <ServiceRow key={k.id} s={k} depth={1} fin={svcFin(k.id)} parentBasis={p.billing_basis} onEdit={onEdit} onRemove={onRemove} />) : []),
          ];
        })}
      </tbody>
    </table>
  );
}
